import { Ref } from 'preact';
import { forwardRef } from 'preact/compat';
import { classNames } from '../utils/UNSAFE_classNames';
import { StyledComponentProps } from '../utils/UNSAFE_typeUtils';
import { useFormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { TextFieldStyles, TextFieldVariantOptions } from './themes/TextFieldStyles.css';
import { useInputGroupContext } from '../hooks/UNSAFE_useInputGroupContext';
import { FormLayoutRedwoodTheme } from './themes/redwood/FormLayoutTheme';
import { LabelRedwoodTheme } from '../UNSAFE_Label/themes/redwood/LabelTheme';
import { TextFieldRedwoodTheme } from './themes/redwood/TextFieldTheme';
import { SkeletonRedwoodTheme } from '../UNSAFE_Skeleton/themes/redwood/SkeletonTheme';
import { layoutSpanStyles, LayoutColumnSpan } from '../utils/UNSAFE_styles/Layout';

export type StyleProps = {
  columnSpan?: LayoutColumnSpan;
  hasInsideLabel?: boolean;
  variant?: 'default' | 'embedded';
};
type Props = StyledComponentProps<'div', StyleProps>;

export const StyledTextField = forwardRef(
  ({ columnSpan = 1, hasInsideLabel = false, variant = 'default', ...props }: Props, ref?: Ref<HTMLElement>) => {
    // because of the mismatch of height for inputs with inside labels and no labels,
    // we'll have to set everything to the same min-height while within an input group context.
    // Here we'll look to see if it's present and apply the style as needed
    const groupContext = useInputGroupContext();
    const { isReadonly } = useFormFieldContext();
    const { baseTheme: formLayoutTheme } = useComponentTheme(FormLayoutRedwoodTheme);
    const { baseTheme: labelBaseTheme } = useComponentTheme(LabelRedwoodTheme);
    const { classes: textFieldClasses, styles } = useComponentTheme<
      TextFieldVariantOptions,
      TextFieldStyles
    >(TextFieldRedwoodTheme, {
      readonly: isReadonly ? 'isReadonly' : 'notReadonly',
      formControlInsideLabel: hasInsideLabel ? 'hasInsideLabel' : 'noInsideLabel',
      withinGroup: groupContext !== null ? 'isWithinGroup' : undefined
    });

    const { formControlBase, textFieldBase, textFieldEmbedded } = styles;

    // because TextField components reference Skeleton scoped vars, we need to bring in the Skeleton base theme
    // to pick up the scoped var definitions.
    const { baseTheme: skeletonBaseTheme } = useComponentTheme(SkeletonRedwoodTheme);

    const classes = classNames([
      skeletonBaseTheme,
      formControlBase,
      textFieldBase,
      variant === 'embedded' && textFieldEmbedded,
      textFieldClasses,
      formLayoutTheme,
      labelBaseTheme,
      layoutSpanStyles.layoutSpanColumn[columnSpan]
    ]);

    return <div {...props} class={classes} ref={ref as Ref<HTMLDivElement> | undefined} />;
  }
);
